import { useCallback, useContext, useMemo } from 'react'
import type { ChangeEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import GeoContext from 'contexts/geoContext'
import Select from 'components/Common/Form/Input/Select'
import Group from 'components/utils/Group'
import { useCanAccessChatQa } from 'tripPlanner/hooks/useCanAccessChatQa'
import { getChatSurfaces } from 'tripPlanner/api'
import { chatSurfaceUrl } from 'tripPlanner/utils/chatSurfaceUrl'

interface Props {
  chatId?: number
  offerId?: string
  disabled?: boolean
}

const TRIP_PLANNER_VALUE = 'trip_planner'

export default function ChatSurfaceSwitcher(props: Props) {
  const allowed = useCanAccessChatQa()
  const { search } = useLocation()
  const isSurfaceEnabled = !!props.offerId || new URLSearchParams(search).get('staffView') === '1'
  return allowed && isSurfaceEnabled && props.chatId ? <SurfaceOptions {...props} chatId={props.chatId} /> : null
}

function SurfaceOptions({ chatId, offerId, disabled }: Props & { chatId: number }) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { search } = useLocation()
  const { data, isError, isLoading } = useQuery({
    queryKey: ['trip-chat-surfaces', chatId, currentRegionCode],
    queryFn: () => getChatSurfaces(chatId, currentRegionCode),
    staleTime: 0,
  })
  const plannerLabel = intl.formatMessage({ defaultMessage: 'TripPlanner', id: 'fmdfxWxLMHVWmbnnbTKw', description: 'Trip Planner destination in the staff chat surface switcher' })
  const options = useMemo(() => {
    const offers = new Map((data?.visitedSurfaces ?? []).filter(surface => surface.surface === 'accommodation_offer_page' && surface.offerId)
      .map(surface => [surface.offerId!, surface.displayName]))
    if (offerId && !offers.has(offerId)) offers.set(offerId, offerId)
    const hasVisitedPlanner = data?.visitedSurfaces.some(surface => surface.surface === 'trip_planner')
    // Keep the current surface selected even before its first turn is recorded.
    const plannerOptions = hasVisitedPlanner || !offerId ? [{ value: TRIP_PLANNER_VALUE, label: plannerLabel }] : []
    return [...plannerOptions, ...[...offers].map(([id, name]) => ({ value: id, label: `${name} (${id})` }))]
  }, [data, offerId, plannerLabel])
  const onChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    window.location.assign(chatSurfaceUrl(
      chatId, currentRegionCode, event.currentTarget.value === TRIP_PLANNER_VALUE ? undefined : event.currentTarget.value,
      new URLSearchParams(search).get('staffView') === '1',
    ))
  }, [chatId, currentRegionCode, search])
  return <Group direction="vertical" gap={4}>
    <Select
      aria-label={intl.formatMessage({ defaultMessage: 'QA surface', id: 'WpYJyQJb7zP6QHBALMML', description: 'Label for the Trip Team control to open a chat on another surface' })}
      value={offerId ?? TRIP_PLANNER_VALUE}
      onChange={onChange}
      disabled={disabled || isLoading || isError}
      optionalIndicatorEnabled={false}
    >
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </Select>
    {isError && <span>{intl.formatMessage({ defaultMessage: 'Could not load chat surfaces. Refresh to retry.', id: 'QPJs-_O52wBqiTAPDnYb', description: 'Error shown when the staff chat surface switcher cannot load its destinations' })}</span>}
  </Group>
}
